import React, {useEffect, useState} from 'react';
import {Container} from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import {useDispatch, useSelector} from 'react-redux';
import FilterHandler from '../../shared/utils/CombinedFilterHandler';
import {deleteUser, getRoles, getUsers, protect, resendRegistrationMail, resetMail, getMetaData} from '../../utils/mainServices';
import {toggleLoader, toggleConfirmationDialog} from '../../shared/actions/setting';
import {useTranslation} from 'react-i18next';
import FeatherIcon from "feather-icons-react";
import {useNavigate} from 'react-router-dom';
import {toast} from "react-toastify";
import ResetPassword from './resetPassword';
import UserForm from './userForm';
import SearchInput from '../../shared/components/SearchInput';
import useColumnSelector from '../../shared/components/useColumnSelector';

const HomePageTable = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [deleteUserId, setDeleteUserId] = useState("");
  const [resendUser, setResendUser] = useState([]);
  const [resetMailUser, setResetMailUser] = useState([]);
  const [protectUser, setProtectUser] = useState([]);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isFromUserVisible, setIsFormUserVisible] = useState(false);
  const [isResetPasswordVisible, setIsResetPasswordVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const itemsPerPage = 12;
  const [configHost, setConfigHost] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const { ColumnSelector, columnFilter, columnFilterReadOnly } = useColumnSelector('dashboard-table');

  const getVisiblePageNumbers = () => {
    const pages = new Set();
    pages.add(0);
    pages.add(totalPages - 1);
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p >= 0 && p < totalPages) pages.add(p);
    }
    for (let p = 1; p <= 2; p++) {
      if (p < totalPages - 1) pages.add(p);
    }
    for (let p = totalPages - 3; p <= totalPages - 2; p++) {
      if (p > 0 && p < totalPages - 1) pages.add(p);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const output = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] !== sorted[i - 1] + 1) output.push('...');
      output.push(sorted[i]);
    }
    return output;
  };



  const confirmationDialog = useSelector(state => {
    return state.setting.confirmationDialog
  });


  const fieldsToFilter = ["id", "lastName", "username", "email", "role", "status", "mobile"];

  const {
    handleFilter,
    handleSearch,
    filteredList,
    reFilter,
    filters
  } = FilterHandler(updateFilteredUsers, users, fieldsToFilter);

  function updateFilteredUsers () {
    setFilteredUsers(filteredList);
    setCurrentPage(0);
  };

  //Fetch data for the table
  useEffect(() => {
    const fetchUsersAndRoles = async () => {
      dispatch(toggleLoader(true));

      try {
        const [userResponse, roleResponse, metadata] = await Promise.all([
          getUsers(),
          getRoles(),
          getMetaData().catch(() => null),
        ]);

        const roleMap = new Map(roleResponse.content.map(role => [role.id, role.name]));
        const updatedUsers = userResponse.content.map(user => ({
          ...user,
          role: roleMap.get(user.userRoleId) || 'N/A',
        }));

        setUsers(updatedUsers);
        setCurrentPage(0);
        setRoles((roleResponse.content || []).slice().sort((a, b) => a.id - b.id));
        const hostFromMeta = metadata?.content?.configuration?.configHost;
        if (hostFromMeta) {
          setConfigHost(hostFromMeta);
        }
      } catch (error) {
        console.error('Error fetching users or roles:', error);
      } finally {
        dispatch(toggleLoader(false));
      }
    };

    fetchUsersAndRoles();
  }, [shouldFetchData]);

  useEffect(() => {
    if (!users) {
      return;
    }
    const hasFilters = filters && Object.keys(filters || {}).length > 0;
    const hasSearch = !!(searchText && searchText.trim());

    if (hasFilters || hasSearch) {
      handleSearch({ target: { value: searchText || "" } });
      reFilter();
    } else {
      setFilteredUsers(users);
    }
  }, [users]);

  //edit User
  function gotoUserEdit(user) {
    setSelectedUser(user)
    setIsFormUserVisible (true)
  }

  const hideFormUser = (state) => {
    setIsFormUserVisible (false)
    setSelectedUser(null)
    if (state) {
      setShouldFetchData(prev => !prev);
    }
  }

  //User details
  const gotoUserView = (user) => {
    navigate(`/user-view/${user.id}`);
  };
   
  //Reset password
  function gotoResetPassword(user) {
    setSelectedUser(user)
    setIsResetPasswordVisible (true)
  }

  const hideResetPassword = () => {
    setIsResetPasswordVisible (false)
    setSelectedUser(null)
  }

  // go to history
  const gotoUserHistory = (user) => {
    navigate(`/user-actions/${user.id}`);
  };


  //Add new User
  function handleAddUser() {
    setIsFormUserVisible (true)
  }

  //delete user
  function handleDeleteUser(user) {
    dispatch(toggleConfirmationDialog({
      isVisible: true,
      type: 'delete',
      confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_USER_WITH_NAME", { name: (user?.lastName || user?.username || user?.email || "") }),
      confirmationDescription: t("confirmation_msg.THE_DELETE_ACTION_WILL_REMOVE_THE_USER_WITH_NAME", { name: (user?.lastName || user?.username || user?.email || "") })
    }));
    setDeleteUserId(user.id);
  }

  //Resend email verification
  function resendVerification(user) {
    dispatch(toggleConfirmationDialog({
      isVisible: true,
      type: 'request',
      confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_RESEND_REGISTRATION_MAIL?"),
      confirmationDescription: t("confirmation_msg.THE_OK_ACTION_WILL_SEND_REGISTRATION_MAIL_TO_USER"),
    }));
    setResendUser(user);
  }  

  //request reset password
  function handleResetMailUser(user) {
    dispatch(toggleConfirmationDialog({
      isVisible: true,
      type: 'request',
      confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_REQUEST_RESET_PASSWORD?"),
      confirmationDescription: t("confirmation_msg.THE_OK_ACTION_WILL_SEND_REQUEST_RESET_PASSWORD"),
    }));
    setResetMailUser(user);
  }


  //protect the user
  function handleProtectUser(user) {
    if (user.userRoleId == 1 || user.protect) {
      toast.error("You cant unlock the data");
      return
    }else{
      dispatch(toggleConfirmationDialog({
        isVisible: true,
        type: 'request',
        confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_PROTECT_THIS_USER?"),
        confirmationDescription: t("confirmation_msg.THE_OK_ACTION_WILL_PROTECT_THIS_USER"),
      }));
      setProtectUser(user);
    }
  }

  //handle confirmation dialog
  useEffect(() => {
    if (!confirmationDialog || !confirmationDialog.onSuccess) {
      return;
    }

    const handleConfirmation = async () => {
        dispatch(toggleLoader(true));
        if (deleteUserId) {
          // Handle Delete User
          try {
            await deleteUser(deleteUserId);
            toast.success("User deleted successfully");
            setShouldFetchData(prev => !prev);
          } catch (error) {
            if (error.response && error.response.status === 422) {
              toast.warn(error.response.data.message);
            } else {
              toast.error("Something went wrong");
            }
          } finally {
            setDeleteUserId("");
            dispatch(toggleLoader(false));
          }
        } else if (resendUser && Object.keys(resendUser).length !== 0) {
          // Handle Resend Email Verification
          try {
            const signUpHost = configHost && /\/sign-up\/?$/.test(configHost)
              ? configHost
              : (configHost ? `${configHost.replace(/\/$/, '')}/sign-up` : `${window.location.origin}/sign-up`);
            await resendRegistrationMail({
              email: resendUser.email,
              mobile: resendUser.mobile,
              host: signUpHost,
            });
            toast.success("Email has been sent successfully");
          } catch (error) {
            if (error.response?.status === 422) {
              toast.error(error.response.data.message);
            } else {
              toast.error("Oops...Email has not been sent");
            }
          } finally {
            setResendUser(null);
            dispatch(toggleLoader(false));

          }
        } else if (resetMailUser && Object.keys(resetMailUser).length !== 0) {
          // Handle Reset Password Email
          try {
            const signUpHost = configHost && /\/sign-up\/?$/.test(configHost)
              ? configHost
              : (configHost ? `${configHost.replace(/\/$/, '')}/sign-up` : `${window.location.origin}/sign-up`);
            const resetHost = signUpHost.replace(/\/sign-up\/?$/, '/reset');
            await resetMail({
              host: resetHost,
              email: resetMailUser.email,
              address: "9, Mary's Road, Colombo 04, Sri Lanka"
              //mobile: resetMailUser.mobile,
            });
            toast.success("Password reset email sent successfully");
          } catch (error) {
            if (error.response?.status === 422) {
              toast.error(error.response.data.message);
            } else {
              toast.error("Oops...Email has not been reset");
            }
          } finally {
            setResetMailUser(null); 
            dispatch(toggleLoader(false));

          }
        } else if (protectUser && Object.keys(protectUser).length !== 0) {
          // Handle Protect User
          try {
            await protect(
              protectUser.id,
              !protectUser.protect,
            );
            toast.success("User has been protected successfully");
            setShouldFetchData(prev => !prev);
          } catch (error) {
            if (error.response?.status === 422) {
              toast.error(error.response.data.message);
            } else {
              toast.error("Oops...User has not been protected");
            }
          } finally {
            setProtectUser(null);
            dispatch(toggleLoader(false));
          }
        }
    };

    handleConfirmation();
  }, [confirmationDialog]);

   const sentenceCase = (s) => {
    const str = String(s);
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div className={"half-list-container"}>
      <Container>
        <div className="single-card m-rl-m-8 p-a-16">
          <div className={'sa-table-flex sa-table-top'}>
            <span className="sa-table-top-left-items">
              <ColumnSelector/>
              <SearchInput
                value={searchText}
                onValueChange={setSearchText}
                onSearch={handleSearch}
                onPageReset={() => setCurrentPage(0)}
                placeholder="Search User"
              />
              <select
                className="sa-filter sa-table-float-left participant-filter"
                onChange={handleFilter}
                name="role"
                value={(filters && filters.role) || ""}
              >
                <option value={""}>
                  {t("title.ROLE")}
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name} ({role.id})
                  </option>
                ))}
              </select>
              <select
                className="sa-filter sa-table-float-left participant-filter"
                onChange={handleFilter}
                name="status"
                value={(filters && filters.status) || ""}
              >
                <option value={""}>
                  {t("title.STATUS")}
                </option>
                <option value="ACTIVE">{t("title.ACTIVE")}</option>
                <option value="INACTIVE">{t("title.INACTIVE")}</option>
                <option value="SUSPENDED">{t("title.SUSPEND")}</option>
              </select>
            </span>
            <div className="sa-table-top-right-items">
              <div className="xin-pull-right">
                <span className="sa-table-icon-flex sa-table-filter-position">
                  <button
                    onClick={handleAddUser}
                    className={`sa-table-btn-secondary sa-table-btn-secondary-wide sa-table-float-right`}>
                    <FeatherIcon
                      icon={`plus`}
                      className={"sa-table-icon-size"}/> Create User
                  </button>
                </span>
              </div>
            </div>
          </div>
          <div id="main3" className="sa-table-container xindicate-calc-height sa-users-table">
            <table className="table table-borderless">
              <thead>
              <tr>
                <th hidden={columnFilterReadOnly("No.")} className={"sa-table-head-sticky"}>No.</th>
                <th hidden={columnFilter("Name")} className={"sa-table-head-sticky"}>Name</th>
                <th hidden={columnFilter("Username")} className={"sa-table-head-sticky"}>Username</th>
                <th hidden={columnFilter("Email")} className={"sa-table-head-sticky"}>Email</th>
                <th hidden={columnFilter("Role")} className={"sa-table-head-sticky"}>Role</th>
                <th hidden={columnFilter("Status")} className={"sa-table-head-sticky"}>Status</th>
                <th hidden={columnFilter("Actions")} className={"sa-table-head-sticky"}>Actions</th>
              </tr>
              </thead>
              <tbody>
              {filteredUsers
                .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
                .map((item, index) => {
                const hasUsername = !!(item.username && String(item.username).trim());
                const isResendDisabled = hasUsername || item.protect;
                const isResetMailDisabled = !hasUsername || item.protect;

                return (
                <tr key={index}>
                  <td hidden={columnFilterReadOnly("No.")} className={'sa-table-data'}>{currentPage * itemsPerPage + index + 1}</td>
                  <td hidden={columnFilter("Name")} className={'sa-table-data'}>{item.lastName}</td>
                  <td hidden={columnFilter("Username")} className={'sa-table-data'}>{item.username}</td>
                  <td hidden={columnFilter("Email")} className={'sa-table-data'} title={item.email}>{item.email}</td>
                  <td hidden={columnFilter("Role")} className={'sa-table-data'}>{item.role}</td>
                  <td hidden={columnFilter("Status")} className={'sa-table-data'}>
                    {item.status === "ACTIVE" ? (
                      <div className="table-status-active"> {sentenceCase(item.status)}</div>
                    ) : item.status === "INACTIVE" ? (
                      <div className="table-status-inactive">{sentenceCase(item.status)}</div>
                    ) : item.status === "SUSPENDED" ? (
                      <div className="table-status-suspended">{sentenceCase(item.status)}</div>
                    ) : (
                      <div className="table-status-active">{item.status}</div>
                    )}
                  </td>
                  <td hidden={columnFilter("Actions")} className={'sa-table-item-align'}>
                    <div className='sa-table-item-flex'>
                    <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.VIEW")}>
                        <FeatherIcon
                          onClick={() => {
                           gotoUserView(item)
                         ; }}
                          icon="eye"
                          disabled={item.role == "superr" }
                          className={`table-action ${item.role == "superr" ? 'disable-btn' : ''}` } />
                      </div>
                      <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.EDIT")}
                            >
                        <FeatherIcon
                          onClick={() => gotoUserEdit(item)}
                          className={`table-action ${item.role == "superr" || item.protect ? 'disable-btn' : ''}` }
                          icon={"edit"}
                          disabled={item.role == "superr" || item.protect}
                        />
                      </div>
                      <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.DELETE")}>
                        <FeatherIcon
                          className={`feather-trash + ${item.references.length !== 0 || item.protect ? 'disable-btn text-lightred' : 'text-red'}`}
                          icon={`trash-2`}
                          onClick={() => {
                            if (!(item.references.length !== 0 || item.protect)) {
                              handleDeleteUser(item);
                            }
                          }}
                        />
                      </div>

                      <Dropdown style={{ position: 'inherit' }}>
                        <Dropdown.Toggle id="dropdown-custom-components-ums-users" className={'more-action-btn'}>
                        <FeatherIcon
                         className={'text-gray'} icon="more-horizontal" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => { if (!item.protect) { gotoUserHistory(item); } }}
                            disabled={item.protect}
                          >
                            <FeatherIcon className="table-action" icon="clock" />
                            <label className="more-actions">{t("button.USER_HISTORY")}</label>
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() => { if (!isResendDisabled) { resendVerification(item); } }}
                            disabled={isResendDisabled}
                          >
                            <FeatherIcon className={`table-action ${isResendDisabled ? 'disabled-action' : ''}`} icon="upload" />
                            <label className="more-actions">{t("button.RESEND_EMAIL_VERIFICATION")}</label>
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() => { if (!isResetMailDisabled) { handleResetMailUser(item); } }}
                            disabled={isResetMailDisabled}
                          >
                      <FeatherIcon className={`table-action ${isResetMailDisabled ? 'disabled-action' : ''}`} icon="mail" />
                            <label className="more-actions">{t("button.PASSWORD_RESET_EMAIL")}</label>
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() => { if (!(item.userRoleId == 1 || item.protect)) { gotoResetPassword(item); } }}
                            disabled={item.userRoleId == 1 || item.protect}
                          >
                            <FeatherIcon className={`table-action ${(item.userRoleId == 1 || item.protect) ? 'disabled-action' : ''}`} icon="key" />
                            <label className="more-actions">{t("button.RESET_PASSWORD")}</label>
                          </Dropdown.Item>

                          <Dropdown.Item onClick={() => { handleProtectUser(item); }}>
                            <FeatherIcon className="table-action" icon={(item.userRoleId == 1 || item.protect) ? 'lock' : 'unlock'} />
                            <label className="more-actions">{item.protect ? t("button.UNLOCK") : t("button.LOCK")}</label>
                          </Dropdown.Item>
                      </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              );
              })}
              </tbody>
            </table>
            {filteredUsers && filteredUsers.length === 0 && (
              <div className={"empty-results mt-5"}>
                <FeatherIcon icon="info"/>
                <div className={"empty-results-text"}>{t("emptyMsg.THERE_ARE_NO_USERS")}</div>
              </div>
            )}
          </div>
          {filteredUsers.length > itemsPerPage && (
            <div className="pagination">
              <button
                className="previous"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
              >
                Previous
              </button>
              {getVisiblePageNumbers().map((p, idx) => (
                typeof p === 'string' ? (
                  <span key={`el-${idx}`} className="ellipsis">…</span>
                ) : (
                  <button
                    key={`p-${p}`}
                    className={`page ${p === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p + 1}
                  </button>
                )
              ))}
              <button
                className="next"
                disabled={(currentPage + 1) >= totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
        {isResetPasswordVisible && <ResetPassword selectedUserData={selectedUser} onClose={hideResetPassword}/>}
        {isFromUserVisible && (<UserForm
                                  selectedUserData={selectedUser}
                                  onClose={hideFormUser}
                                  formType={selectedUser ? "edit" : "add"}
                                  allRoles={selectedUser ? null : roles}
                                />
      )}
      </Container>
    </div>
  );
};

export default HomePageTable;
